/**
 * /earthworks/ai-quotes — AI-drafted quotes (EW-3, fieldiq-api 0583).
 *
 * Every row is a DRAFT until a person opens it, checks the AI's readings and
 * creates the quote. The subtotal is the API's, priced from the rate card; an
 * unready draft shows `—`, never 0. Admin and office staff only, as the API is.
 */
import { useNavigate } from 'react-router-dom';
import { Bot, Plus } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { useAiBudget, useEwAiRuns } from '../../hooks/useEwAiQuotes';
import { canAdminEw, fmtMoney } from '../../lib/earthworks';
import type { EwAiRun, EwAiStatus } from '../../types/ewAiQuote';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';
import { EW_EYEBROW, EwBadge, EwEmpty, EwPanel, EwRegister, type EwColumn } from './EwBits';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstantDay } from '../../lib/calendar-date';
import type { NdtSkin } from '../../lib/ndt-inspection';
import { fmtCurrency } from '../../lib/display-labels';

export const EW_AI_KIND_LABEL: Record<string, string> = { photo_demo: 'Photo → demolition', plans_dig: 'Plans → dig' };

const STATUS_SKIN: Record<EwAiStatus, NdtSkin> = {
  queued: { label: 'Queued', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  processing: { label: 'AI reading', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  drafted: { label: 'Draft — review', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  failed: { label: 'Needs attention', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  quoted: { label: 'Quoted', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
export const ewAiStatusSkin = (s: EwAiStatus): NdtSkin => STATUS_SKIN[s] ?? { label: s, fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

/** AI usage is always billed in US dollars, whatever the tenant's currency. */
const usd = (n: number | null | undefined) => fmtCurrency(n, 'USD');

export default function EwAiQuotesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canUse = canAdminEw(user?.role);
  const runsQuery = useEwAiRuns(canUse);
  const budget = useAiBudget(canUse);
  const runs = runsQuery.data ?? [];
  const b = budget.data ?? null;
  const tz = useTenantTimeZone();

  const columns: EwColumn<EwAiRun>[] = [
    { key: 'title', header: 'Draft', render: (r) => (<><p className="font-bold text-white">{r.title || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{EW_AI_KIND_LABEL[r.kind] ?? r.kind}</p></>) },
    { key: 'customer', header: 'Customer', render: (r) => (<>{r.customerName || '—'}{r.siteName ? <p className="text-xs text-fieldiq-muted">{r.siteName}</p> : null}</>) },
    { key: 'status', header: 'Status', render: (r) => <EwBadge skin={ewAiStatusSkin(r.status)} value={r.status} /> },
    { key: 'subtotal', header: 'Subtotal', align: 'right', render: (r) => <span className="font-mono">{fmtMoney(r.subtotal ?? null, r.currency ?? null)}</span> },
    { key: 'quote', header: 'Quote', render: (r) => r.quoteNumber || '—' },
    { key: 'created', header: 'Created', render: (r) => formatInstantDay(r.createdAt, tz) ?? '—' },
  ];

  return (
    <div data-testid="ew-ai-quotes-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="AI Quotes"
        description="Upload building photos or plans. The AI reads what it can see, and the rate card prices every line. Each one is a draft until you check it."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Bot className="size-4" aria-hidden="true" />, label: 'Drafts', tone: 'neutral', loading: runsQuery.isLoading, value: runsQuery.isLoading ? undefined : String(runs.length) },
          { icon: <Bot className="size-4" aria-hidden="true" />, label: 'To review', tone: 'accent', loading: runsQuery.isLoading, value: runsQuery.isLoading ? undefined : String(runs.filter((r) => r.status === 'drafted').length) },
          { icon: <Bot className="size-4" aria-hidden="true" />, label: 'AI this month', tone: 'neutral', loading: budget.isLoading, value: b ? `${usd(b.countedUsd)} of ${usd(b.capUsd)}` : '—' },
        ]}
        actions={
          canUse ? (
            <button type="button" className={DH_BAND_SECONDARY} onClick={() => navigate('/earthworks/ai-quotes/new')}>
              <Plus className="size-4" aria-hidden="true" />
              New AI quote
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canUse ? (
            <EwPanel eyebrow="AI quotes" title="Drafts">
              <EwEmpty>AI quoting prices from the rate card, so only admins and office staff use it.</EwEmpty>
            </EwPanel>
          ) : (
            <EwPanel eyebrow="AI quotes" title="Drafts" testId="ew-ai-register" footer={!runsQuery.isLoading && runs.length ? `${runs.length} draft${runs.length === 1 ? '' : 's'}` : undefined}>
              <EwRegister
                query={runsQuery}
                rows={runs}
                columns={columns}
                rail={(r) => ewAiStatusSkin(r.status).rail}
                onOpen={(r) => openRecord(`/earthworks/ai-quotes/${r.id}`)}
                subject="AI quote drafts"
                testId="ew-ai-run"
                minWidth="min-w-[900px]"
                empty={<EwEmpty>No AI quotes yet. Start one from building photos or a set of plans.</EwEmpty>}
                card={(r) => (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-white">{r.title || '—'}</p>
                        <p className="mt-0.5 text-xs text-fieldiq-muted">{EW_AI_KIND_LABEL[r.kind] ?? r.kind} · {r.customerName || '—'}</p>
                      </div>
                      <EwBadge skin={ewAiStatusSkin(r.status)} value={r.status} />
                    </div>
                    <p className="mt-2 font-mono text-sm text-white">{fmtMoney(r.subtotal ?? null, r.currency ?? null)}</p>
                  </>
                )}
              />
            </EwPanel>
          )}
        </div>
      </section>
    </div>
  );
}
